"use client";

import { presentationType } from "@/lib/systemProperties";
import type { BoardProperty, BoardProperties } from "@/types/board";
import { PropertyIcon } from "@/components/tailwind/board/PropertyIcon";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";
import { FormulaPropertyInput } from "@/components/tailwind/board/properties/inputs/formulaPropertyInput";
import { Tag } from "lucide-react";
import { formatDateCellLabel } from "@/lib/date-value";
import { formatFormulaValue } from "@/utils/formatFormulaValue";
import { PropertyOptionDisplay, type OptionType } from "@/components/tailwind/board/PropertyOptionDisplay";
import { MemberAvatar } from "@/components/tailwind/ui/memberAvatar";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useUserTimeZone } from "@/hooks/use-user-timezone";

function FileLikePropertyPreview({ value }: { value: unknown }) {
  const empty = (
    <span className="inline-block px-2 py-1.5 text-sm text-gray-400 dark:text-gray-500">Empty</span>
  );
  const files = Array.isArray(value) ? value : value ? [value] : [];
  if (files.length === 0) return empty;
  return (
    <div className="w-full space-y-0.5 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100">
      {files.map((f: unknown, i: number) => (
        <div key={i} className="truncate">
          {typeof f === "object" && f !== null && "name" in f
            ? String((f as { name?: string }).name)
            : String(f)}
        </div>
      ))}
    </div>
  );
}

export function LayoutEditorPropertyValuePreview({
  property,
  value,
  formulaError,
  resolveRelationTitle,
}: {
  property: BoardProperty;
  value: unknown;
  formulaError?: string;
  /** Resolves a related page id to its title; unresolved ids are skipped. */
  resolveRelationTitle?: (noteId: string, linkedDatabaseId: string) => string;
}) {
  const { timeZone } = useUserTimeZone();
  const { getMemberName } = useWorkspaceContext();
  const empty = (
    <span className="inline-block px-2 py-1.5 text-sm text-gray-400 dark:text-gray-500">Empty</span>
  );

  switch (presentationType(property.type)) {
    case "checkbox":
      return (
        <div className="flex w-full items-center px-2 py-1.5">
          <input
            type="checkbox"
            checked={!!value}
            readOnly
            tabIndex={-1}
            className="pointer-events-none mx-2 opacity-90"
          />
        </div>
      );

    case "status": {
      const options = property.options ?? [];
      const selected = options.find((o) => String(o.id) === String(value));
      if (!selected) return empty;
      return (
        <div className="flex w-full flex-wrap items-center gap-1.5 rounded-sm px-2 py-1.5">
          <PropertyOptionDisplay type="status" propSchema={property} value={value} />
        </div>
      );
    }

    case "priority":
    case "select": {
      const options = property.options ?? [];
      const selected = options.find((o) => String(o.id) === String(value));
      if (!selected) {
        return value !== null && value !== undefined && value !== "" ? (
          <div className="w-full px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100">{String(value)}</div>
        ) : (
          empty
        );
      }
      return (
        <div className="flex w-full flex-wrap items-center gap-1.5 rounded-sm px-2 py-1.5">
          <PropertyOptionDisplay type={property.type as OptionType} propSchema={property} value={value} />
        </div>
      );
    }

    case "multi_select": {
      const ids = Array.isArray(value) ? value : value ? [value as string] : [];
      if (ids.length === 0) return empty;
      return (
        <div className="flex w-full flex-wrap items-center gap-1.5 rounded-sm px-2 py-1.5">
          <PropertyOptionDisplay type="multi_select" propSchema={property} value={value} maxItems={Infinity} />
        </div>
      );
    }

    case "date": {
      const label = formatDateCellLabel(value, property.timeFormat, { column: property.timeZone, viewer: timeZone });
      if (!label) return empty;
      return (
        <div className="w-full px-2 py-1.5">
          <span className="text-sm text-gray-900 dark:text-gray-100">{label}</span>
        </div>
      );
    }

    case "formula":
      return (
        <div className="w-full px-2 py-1.5">
          <FormulaPropertyInput
            value={value}
            property={property}
            errorMessage={formulaError}
            className="flex w-full items-center px-0 py-0 text-sm"
          />
        </div>
      );

    case "number": {
      if (value === null || value === undefined || value === "") return empty;
      const opts = {
        numberFormat: (property as { numberFormat?: string }).numberFormat,
        decimalPlaces: (property as { decimalPlaces?: number }).decimalPlaces,
      };
      const formatted = formatFormulaValue(value, "number", opts);
      return (
        <div className="w-full px-2 py-1.5">
          <span className="text-sm text-gray-900 dark:text-gray-100">{formatted}</span>
        </div>
      );
    }

    case "person": {
      const people = Array.isArray(value) ? value : [];
      if (people.length === 0) return empty;
      return (
        <div className="flex w-full flex-wrap gap-2.5 px-2 py-1.5">
          {people.map((p: any, i: number) => {
            // Person values are member objects ({ userId, userName, userEmail });
            // they may also be a bare id string. Resolve the live name/avatar so
            // we never render "[object Object]".
            const isObj = typeof p === "object" && p !== null;
            const userId = isObj ? p.userId : typeof p === "string" ? p : undefined;
            const userEmail = isObj ? p.userEmail : undefined;
            const storedName = isObj ? p.userName || p.name : undefined;
            const displayName =
              getMemberName(userId) ||
              getMemberName(userEmail) ||
              storedName ||
              userEmail ||
              (typeof p === "string" ? p : "Unknown");
            return (
              <span
                key={`${userId ?? i}-${i}`}
                className="flex items-center gap-1 text-xs text-gray-800 dark:text-gray-200"
              >
                <MemberAvatar
                  name={displayName}
                  email={userEmail}
                  userId={userId}
                  className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-[9px] text-gray-600 dark:bg-gray-600 dark:text-gray-300"
                />
                {displayName}
              </span>
            );
          })}
        </div>
      );
    }

    case "relation": {
      const items = Array.isArray(value) ? value : [];
      if (items.length === 0) return empty;
      const linkedDatabaseId = property.linkedDatabaseId ? String(property.linkedDatabaseId) : "";
      // Values are page ids — resolve them, never render the raw id.
      const labels = items
        .map((item: unknown) => {
          if (typeof item === "object" && item !== null) {
            const o = item as { title?: string; name?: string; label?: string };
            return o.title ?? o.name ?? o.label ?? "";
          }
          const noteId = String(item ?? "");
          if (!noteId) return "";
          return resolveRelationTitle ? resolveRelationTitle(noteId, linkedDatabaseId) : "";
        })
        .filter(Boolean);
      if (labels.length === 0) return empty;
      return (
        <div className="flex w-full flex-wrap gap-1 px-2 py-1.5">
          {labels.map((label: string, i: number) => {
            return (
              <span
                key={i}
                className="max-w-full truncate rounded-md border border-component px-2 py-0.5 text-xs"
              >
                {label}
              </span>
            );
          })}
        </div>
      );
    }

    case "file":
      return <FileLikePropertyPreview value={value} />;

    case "text":
      if (value === null || value === undefined || String(value).trim() === "") return empty;
      return (
        <div className="w-full px-2 py-1.5">
          <span className="text-sm text-gray-900 dark:text-gray-100">{String(value)}</span>
        </div>
      );

    default: {
      if ((property as { type?: string }).type === "files_media") {
        return <FileLikePropertyPreview value={value} />;
      }
      if (value === null || value === undefined || value === "") return empty;
      if (typeof value === "object") {
        const v = value as { name?: string; title?: string; label?: string };
        const label = v.name ?? v.title ?? v.label;
        if (label) {
          return (
            <div className="w-full px-2 py-1.5">
              <span className="text-sm text-gray-900 dark:text-gray-100">{String(label)}</span>
            </div>
          );
        }
        return (
          <div className="w-full px-2 py-1.5">
            <span className="truncate font-mono text-xs text-gray-800 dark:text-gray-200">{JSON.stringify(value)}</span>
          </div>
        );
      }
      return (
        <div className="w-full px-2 py-1.5">
          <span className="whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-gray-100">
            {String(value)}
          </span>
        </div>
      );
    }
  }
}

export function LayoutEditorPropertiesPreviewRows({
  boardProperties,
  propertyOrder,
  databaseProperties,
  formulaErrors,
  hiddenPropertyIds = [],
  resolveRelationTitle,
}: {
  boardProperties: BoardProperties;
  propertyOrder: string[];
  databaseProperties: Record<string, unknown>;
  formulaErrors?: Record<string, string>;
  hiddenPropertyIds?: string[];
  resolveRelationTitle?: (noteId: string, linkedDatabaseId: string) => string;
}) {
  const hiddenSet = new Set(hiddenPropertyIds);
  const keysFromOrder = propertyOrder.filter((k) => boardProperties[k] && !hiddenSet.has(k));
  const rest = Object.keys(boardProperties).filter((k) => !keysFromOrder.includes(k) && !hiddenSet.has(k));
  const orderedKeys = [...keysFromOrder, ...rest];

  return (
    <div className="w-full bg-transparent pb-3 pt-1">
      <div className="relative w-full bg-transparent" role="table" aria-label="Layout preview properties">
        {orderedKeys.map((key) => {
          const property = boardProperties[key];
          if (!property) return null;
          const raw = databaseProperties[key];
          return (
            <div key={key} className="relative mx-3 mb-1.5 flex gap-4" role="row">
              <div className="relative shrink-0">
                <div className="flex min-w-0 max-w-40 w-40 items-center rounded-sm bg-transparent px-3 py-2">
                  <div className="flex min-w-0 items-center text-gray-600 dark:text-gray-400">
                    <div className="mr-2 shrink-0 text-gray-500">
                      {property.icon
                        ? renderIcon(property.icon, { style: { width: 16, height: 16 } })
                        : <PropertyIcon type={property.type} size={16} className="scale-110 transform" fallback={Tag} />}
                    </div>
                    <span className="truncate text-sm font-medium">
                      {property.name || ""}
                    </span>
                  </div>
                </div>
              </div>
              <div className="relative flex min-w-0 flex-1 items-center">
                <LayoutEditorPropertyValuePreview
                  property={property as BoardProperty}
                  value={raw}
                  formulaError={formulaErrors?.[key]}
                  resolveRelationTitle={resolveRelationTitle}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
